// utils/examples/bookingExamples.ts
//
// The single source for /examples/booking: what each recipe is, which live demo
// page it books against, and the exact block a visitor copies. Nothing here is
// illustrative — every id is a real document in the Apex Auto Spa demo
// workspace, asserted against the production API by
// scripts/booking-examples-preflight.mjs (plan
// 2026-09-07-booking-mount-api-phone-e164-live-examples, C3).
//
// The page renders these; it invents no attribute of its own, so the unit tests
// on this module are the whole contract for what visitors are shown.

/** Bookings made from these examples are test data in the demo workspace. */
export const DEMO_WORKSPACE_ID = 'demo_showcase_apex_auto_spa'
export const BOOKING_SCRIPT_URL = 'https://369.dev/booking.js'
const PUBLIC_BASE = 'https://369.dev/ws'

/** Every demo page the examples page or the docs link to (contract C3). */
export const BOOKING_EXAMPLE_PAGES = [
  'book',
  'round-robin-team',
  'choose-specialist',
  'intake-forms',
  'deposit-review',
  'buffer-limits',
  'calendar-first-no-images',
  'large-gallery',
  'theme-corporate',
  'theme-dark',
  'theme-luxury',
  'theme-nord',
  'theme-emerald',
  'theme-cupcake',
] as const
export type BookingExamplePage = typeof BOOKING_EXAMPLE_PAGES[number]

/**
 * Every attribute `booking.js` reads. The examples must not show anything
 * outside this set — a visitor copying an invented attribute gets silence, not
 * an error.
 */
export const BOOKING_DATA_ATTRIBUTES = [
  'data-booking',
  'data-workspace',
  'data-page',
  'data-mode',
  'data-label',
  'data-color',
  'data-service',
  'data-member',
  'data-theme',
  'data-lang',
  'data-prefill-name',
  'data-prefill-email',
  'data-prefill-phone',
  'data-prefill-notes',
  'data-intake',
  'data-metadata',
  'data-step',
  'data-w369-booking',
] as const

/** The two articles this page defers to for the complete reference. */
export const BOOKING_DOCS_LINKS = [
  {
    href: 'https://docs.workspace369.com/v/2026.3/scheduling/embed-booking-on-your-website/',
    label: 'Embed booking on your website',
    note: 'Every attribute, the JavaScript API, and the troubleshooting list.',
  },
  {
    href: 'https://docs.workspace369.com/v/2026.3/scheduling/set-up-a-booking-embed-with-an-ai-assistant/',
    label: 'Set up a booking embed with an AI assistant',
    note: 'The whole contract in one page, written to paste into ChatGPT, Claude or Copilot.',
  },
] as const

/** Six representative daisyUI themes; the full list of 35 lives in the docs. */
export const BOOKING_EXAMPLE_THEMES = ['corporate', 'dark', 'luxury', 'nord', 'emerald', 'cupcake'] as const

/** Pages that demonstrate a layout or a scheduling rule rather than an embed style. */
export const BOOKING_EXAMPLE_LAYOUT_PAGES = [
  { slug: 'buffer-limits', label: 'Buffers and daily limits', note: 'Calendar-first layout' },
  { slug: 'calendar-first-no-images', label: 'Calendar first', note: 'No service images' },
  { slug: 'large-gallery', label: 'Large service gallery', note: 'Services-first layout' },
] as const satisfies ReadonlyArray<{ slug: BookingExamplePage, label: string, note: string }>

/** `https://369.dev/ws/<workspace>/book[/<slug>]` — the default page has no slug. */
export function bookingExamplePublicUrl(page: BookingExamplePage): string {
  const base = `${PUBLIC_BASE}/${DEMO_WORKSPACE_ID}/book`
  return page === 'book' ? base : `${base}/${page}`
}

/**
 * Every line a visitor READS on /examples/booking is at most two short
 * sentences of plain words (live QA, addendum 3 item 5). No em-dash chains, no
 * `<head>`, no `mount()` — the complete reference is one click away in the two
 * docs articles, and a caption that needs a paragraph is a caption that has
 * stopped explaining. bookingExamples.test.ts holds the line.
 */
export const EXAMPLE_PROSE_MAX = 200
export const EXAMPLE_PROSE_MAX_SENTENCES = 2

export interface BookingExample {
  /** Stable anchor id; also the section's `#hash`. */
  id: string
  title: string
  /** When a site should reach for this. */
  summary: string
  /** The caption over the live demo: what the thing below actually does. */
  setup: string
  page: BookingExamplePage
  /** The copy-paste block. */
  code: string
  /** The one thing worth knowing before pasting. */
  notes?: string
  publicUrl: string
}

function example(input: Omit<BookingExample, 'publicUrl'>): BookingExample {
  return { ...input, publicUrl: bookingExamplePublicUrl(input.page) }
}

/**
 * The credit anchor that ships INSIDE the plain-iframe snippet, byte for byte
 * what the app's share sheet copies (BookingEmbedSnippets.iframe in
 * lib/view/schedules/booking_pages/booking_embed_snippets.dart). A bare iframe
 * has no script to inject the credit, so the anchor is part of the block —
 * which is why the live iframe demo on /examples/booking renders it too, with
 * the same markup rather than a hand-built lookalike.
 */
export const IFRAME_CREDIT = '<a href="https://workspace369.com/?utm_source=powered_by&amp;utm_medium=booking&amp;utm_campaign=powered-by&amp;utm_content=iframe" target="_blank" rel="noopener" style="display:inline-flex;align-items:center;gap:6px;margin-top:8px;font:500 12px/1 system-ui,sans-serif;color:#6b7280;text-decoration:none"><img src="https://369.dev/logo/favicon-32x32.png" width="14" height="14" alt=""> Powered by <strong>Workspace369</strong></a>'

export const bookingExamples: BookingExample[] = [
  example({
    id: 'inline',
    title: 'Inline on the page',
    summary: 'The scheduler sits in the page and grows to fit. Best where booking is the point of the page.',
    setup: 'The scheduler below is the Apex Auto Spa booking page: three services, instant booking.',
    page: 'book',
    code: `<script src="${BOOKING_SCRIPT_URL}" data-booking
  data-workspace="${DEMO_WORKSPACE_ID}" async></script>`,
    notes: 'Paste the inline snippet in the body of your page, where the scheduler should appear.',
  }),
  example({
    id: 'popup',
    title: 'Popup button',
    summary: 'A button in the corner of every page opens the scheduler over your content. Best when booking is a secondary action.',
    setup: 'A floating button on every page. Click it, or the button in the corner of this page.',
    page: 'round-robin-team',
    code: `<script src="${BOOKING_SCRIPT_URL}" data-booking
  data-workspace="${DEMO_WORKSPACE_ID}"
  data-page="round-robin-team"
  data-mode="popup" data-label="Book a detail" data-color="#111827" async></script>`,
    notes: 'This page has two specialists and picks whoever is free.',
  }),
  example({
    id: 'own-trigger',
    title: 'Your own button or link',
    summary: 'Your own button, link or menu item opens the scheduler. Nothing of ours shows on the page.',
    setup: 'The link below is a real trigger. It opens the page where the visitor picks a specialist.',
    page: 'choose-specialist',
    code: `<script src="${BOOKING_SCRIPT_URL}" data-booking
  data-workspace="${DEMO_WORKSPACE_ID}"
  data-page="choose-specialist"
  data-mode="api" async></script>

<a href="${bookingExamplePublicUrl('choose-specialist')}" data-w369-booking>
  Choose your specialist
</a>`,
    notes: 'Any button or link opens the scheduler. Add data-w369-booking to it and keep a real link for visitors without JavaScript.',
  }),
  example({
    id: 'own-form',
    title: 'Your own form, then the scheduler',
    summary: 'Ask for the details in your own form, then hand them over. The visitor never types their name twice.',
    setup: 'Fill the form below. The scheduler opens on the time picker with your answers in it.',
    page: 'book',
    code: `<script src="${BOOKING_SCRIPT_URL}" data-booking
  data-workspace="${DEMO_WORKSPACE_ID}"
  data-service="demo_booking_express_wash"
  data-mode="api" async></script>

<form id="lead-form">
  <input name="name" placeholder="Your name" required>
  <input name="email" type="email" placeholder="you@company.com" required>
  <!-- Phone numbers need their country code -->
  <input name="phone" type="tel" placeholder="+14155550100" required>
  <input name="company" placeholder="Company">
  <button type="submit">Pick a time</button>
</form>

<script>
  document.getElementById('lead-form').addEventListener('submit', function (event) {
    event.preventDefault();
    var answers = new FormData(event.target);
    W369Booking.open({
      step: 'time',
      layout: 'minimal',
      prefill: {
        name: answers.get('name'),
        email: answers.get('email'),
        phone: answers.get('phone'),
        metadata: { company: answers.get('company'), source: 'examples' }
      }
    });
  });
</script>`,
    notes: 'Your form collects the details; the scheduler opens on the time picker with them filled in. Anything in metadata lands on the booking and the client record.',
  }),
  example({
    id: 'own-modal',
    title: 'Inside your own modal',
    summary: 'Put the scheduler inside something you own: a dialog, a drawer, a step in your checkout.',
    setup: 'The button below opens our own dialog with the scheduler inside it.',
    page: 'intake-forms',
    code: `<script src="${BOOKING_SCRIPT_URL}" data-booking
  data-workspace="${DEMO_WORKSPACE_ID}"
  data-page="intake-forms" data-mode="api" async></script>

<button type="button" id="open-modal">Book a detail</button>

<dialog id="booking-modal">
  <button type="button" id="close-modal" aria-label="Close">Close</button>
  <div id="modal-slot"></div>
</dialog>

<script>
  // booking.js loads async, so wait for it before calling mount().
  function w369Ready(fn) {
    if (window.W369Booking && typeof window.W369Booking.mount === 'function') fn(window.W369Booking);
    else window.addEventListener('w369:ready', function (e) { fn(e.detail.api); }, { once: true });
  }

  var modal = document.getElementById('booking-modal');
  var handle = null;

  document.getElementById('open-modal').addEventListener('click', function () {
    modal.showModal();
    w369Ready(function (api) {
      handle = api.mount('#modal-slot', {
        step: 'time',
        layout: 'minimal',
        accent: '#111827',
        // lang: 'pl' would show it in Polish; without it the visitor's browser decides.
        prefill: {
          name: 'Ada Lovelace',
          email: 'ada@example.com',
          phone: '+14155550100',
          intake: {
            vehicle_year_make_model: '2022 Tesla Model 3',
            detail_priorities: ['Odor removal', 'Leather conditioning']
          }
        }
      });
    });
  });

  document.getElementById('close-modal').addEventListener('click', function () { modal.close(); });

  modal.addEventListener('close', function () {
    if (handle) handle.destroy();
    handle = null;
  });
</script>`,
    notes: 'Render the scheduler inside your own dialog with mount(); call destroy() when it closes.',
  }),
  example({
    id: 'plain-iframe',
    title: 'Plain iframe',
    summary: 'One iframe, no script. For a site builder or CMS that blocks custom JavaScript.',
    setup: 'The frame below is a page that needs approval before anything reaches the calendar.',
    page: 'deposit-review',
    code: `<iframe src="${bookingExamplePublicUrl('deposit-review')}?embed=1" title="Book an appointment" width="100%" height="900" style="border:0;display:block" loading="lazy"></iframe>\n${IFRAME_CREDIT}`,
    notes: 'One iframe, fixed height, no script. This page needs approval, so a booking here waits for the team.',
  }),
  example({
    id: 'layouts-and-themes',
    title: 'Layouts and themes',
    summary: 'The booking page sets the layout; the embed can set the colours.',
    setup: 'Every page has a layout and a theme. Open a few to compare.',
    page: 'large-gallery',
    code: `<script src="${BOOKING_SCRIPT_URL}" data-booking
  data-workspace="${DEMO_WORKSPACE_ID}"
  data-page="large-gallery"
  data-theme="nord" async></script>`,
    notes: 'data-theme takes any of the 35 theme names. Leave it off and the page keeps the theme it was given in the app.',
  }),
  example({
    id: 'language',
    title: 'Language',
    summary: 'The scheduler follows the language of the visitor\'s browser. Set data-lang to show one language on every visit.',
    setup: 'The scheduler below is the same booking page in Polish.',
    page: 'book',
    code: `<script src="${BOOKING_SCRIPT_URL}" data-booking
  data-workspace="${DEMO_WORKSPACE_ID}"
  data-lang="pl" async></script>`,
    notes: 'data-lang takes a language code such as pl, de or pt-PT. Leave it off and the page follows the language of the visitor\'s browser.',
  }),
]
